import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment';
import { debounce, first, map, Observable, pairwise, startWith, Subscription, tap, timer } from 'rxjs';
import {
  NewProviderFormValues,
  Provider,
  ProviderVisit,
  ProviderVisitVerification,
  UsState,
} from 'src/app/core/models';
import { LookupState } from 'src/app/core/store/lookup/lookup.state';
import {
  SetNewProviderUserData,
  SetProviderUserData,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { ProviderUserData } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { ApiStoreData, CcsSentryError } from 'src/app/core/store/utils';
import {
  maxDateValidator,
  minDateValidator,
  phoneValidator,
  validateAllFormFields,
  zipCodeValidator,
} from 'src/app/core/utils/validators';
import { FormFieldOption } from 'src/app/shared/form-field/form-field.component';
import { asFormControl } from 'src/app/core/utils/common';

@Component({
  selector: 'app-provider-step',
  templateUrl: './provider-step.component.html',
  styleUrls: ['./provider-step.component.scss'],
})
export class ProviderStepComponent implements OnInit, OnDestroy {
  @Select(OrderWizardDataState.isProvidersStepLoading) isProvidersStepLoading$!: Observable<boolean>;
  @Select(OrderWizardDataState.providersStepError) providersStepError$!: Observable<CcsSentryError | null>;

  @Select(OrderWizardDataState.providers) providers$!: Observable<Provider[]>;
  @Select(OrderWizardDataState.providerVisitVerification)
  providerVisitVerification$!: Observable<ProviderVisitVerification>;
  @Select(OrderWizardDataState.providerVisits) providerVisits$!: Observable<ProviderVisit[]>;
  @Select(LookupState.usStates) usStates$!: Observable<UsState[]>;
  @Select(OrderWizardDataState.providerUserData) providerUserData$!: Observable<ProviderUserData | null>;
  @Select(OrderWizardDataState.initialTargetShipDate) initialTargetShipDate$!: Observable<string | null>;
  @Select(OrderWizardDataState.lastProviderVisitValidationState) lastProviderVisitValidationState$!: Observable<
    ApiStoreData<boolean | null>
  >;
  @Select(OrderWizardDataState.nextProviderVisitValidationState) nextProviderVisitValidationState$!: Observable<
    ApiStoreData<boolean | null>
  >;

  providerForm = new FormGroup({
    selectedProviderId: new FormControl(null, Validators.required),
    providerVisitFormValues: new FormGroup({
      lastProviderVisit: new FormControl(null),
      hasUpcomingVisit: new FormControl(null),
      nextProviderVisit: new FormControl(null),
    }),
  });

  newProviderForm = new FormGroup({
    providerFirstName: new FormControl(null, [Validators.required]),
    providerLastName: new FormControl(null, [Validators.required]),
    npi: new FormControl(null),
    phoneNumber: new FormControl(null, [Validators.required, phoneValidator()]),
    addressLine1: new FormControl(null),
    addressLine2: new FormControl(null),
    addressCity: new FormControl(null),
    addressState: new FormControl(null),
    addressZip: new FormControl(null, [zipCodeValidator()]),
  });

  providerVisits: ProviderVisit[] | null = null;
  providerVisitVerification: ProviderVisitVerification | null = null;
  initialTargetShipDate: string | null = null;

  displayModal: boolean = false;
  today = moment().startOf('day');

  usStates: FormFieldOption[] = [];

  newProviderFormInitialValues: NewProviderFormValues | null = null;

  formListener: Subscription | null = null;

  asFormControl = asFormControl;

  constructor(private store: Store) {}

  ngOnInit() {
    this.setInitialValues();
    this.setSelectedProviderListener();
    this.setLastProviderVisitValidationListener();
    this.setHasUpcomingVisitListener();

    // Must be called last after all listeners have been established
    // in order to trigger the listners with initial values upon calling setValue()
    this.providerUserData$.pipe(first()).subscribe((initialFormValues) => {
      if (initialFormValues) {
        const { selectedProviderId, providerVisitFormValues, newProviderFormValues } = initialFormValues;
        const { lastProviderVisit, hasUpcomingVisit, nextProviderVisit } = providerVisitFormValues;
        this.providerForm.setValue({
          selectedProviderId,
          providerVisitFormValues: { lastProviderVisit, hasUpcomingVisit, nextProviderVisit },
        });
        if (newProviderFormValues) {
          this.newProviderForm.setValue(newProviderFormValues);
          this.newProviderFormInitialValues = newProviderFormValues;
        }
      }
    });

    this.formListener = this.providerForm.valueChanges.pipe(debounce(() => timer(100))).subscribe((form) => {
      this.store.dispatch(new SetProviderUserData(form));
    });
  }

  ngOnDestroy() {
    if (this.formListener) {
      this.formListener.unsubscribe();
    }
  }

  setInitialValues() {
    this.providerVisitVerification$
      .pipe(
        tap((pvv) => {
          this.providerVisitVerification = pvv;
          if (pvv?.required) {
            this.providerForm
              .get('providerVisitFormValues')
              ?.get('lastProviderVisit')
              ?.setValidators([Validators.required, maxDateValidator(this.today.clone().add(1, 'day'))]);
          }
        })
      )
      .subscribe();

    this.providerVisits$
      .pipe(
        tap((visits) => {
          this.providerVisits = visits;
        })
      )
      .subscribe();

    this.usStates$
      .pipe(
        tap((usStates) => {
          this.usStates = usStates.map((s) => ({
            label: s.name,
            value: s.value,
          }));
        })
      )
      .subscribe();

    this.initialTargetShipDate$.subscribe((itsd) => {
      this.initialTargetShipDate = itsd;
    });
  }

  setSelectedProviderListener() {
    this.providerForm.get('selectedProviderId')?.valueChanges.subscribe((pId) => {
      let lastProviderVisit = null;

      if (pId !== null && this.providerVisits) {
        const visits = this.providerVisits.filter((pv) => pv.doctorId === pId);

        if (visits.length > 0) {
          lastProviderVisit = visits.reduce((acc, pv) => (pv.visitDate > acc.visitDate ? pv : acc)).visitDate;
        }
      }

      this.providerForm.get('providerVisitFormValues')?.markAsPristine();
      this.providerForm.get('providerVisitFormValues')?.markAsUntouched();

      this.providerForm.patchValue({
        providerVisitFormValues: {
          lastProviderVisit,
          hasUpcomingVisit: null,
          nextProviderVisit: null,
        },
      });
    });
  }

  setLastProviderVisitValidationListener() {
    this.lastProviderVisitValidationState$
      .pipe(
        map((lpvValidationState) => lpvValidationState.data === null || lpvValidationState.data === true),
        startWith(true),
        pairwise()
      )
      .subscribe(([prev, next]) => {
        if (prev === next) return;
        const isLpvValid = next;

        if (isLpvValid) {
          this.providerForm.get('providerVisitFormValues')?.get('hasUpcomingVisit')?.setValidators([]);
          this.providerForm.get('providerVisitFormValues')?.get('hasUpcomingVisit')?.markAsPristine();
          this.providerForm.get('providerVisitFormValues')?.get('hasUpcomingVisit')?.markAsUntouched();

          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.setValidators([]);
          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.markAsPristine();
          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.markAsUntouched();

          this.providerForm.patchValue({
            providerVisitFormValues: { hasUpcomingVisit: null, nextProviderVisit: null },
          });
        } else {
          this.providerForm
            .get('providerVisitFormValues')
            ?.get('hasUpcomingVisit')
            ?.setValidators([Validators.required]);
        }
      });
  }

  setHasUpcomingVisitListener() {
    this.providerForm
      .get('providerVisitFormValues')
      ?.get('hasUpcomingVisit')
      ?.valueChanges.subscribe((huv) => {
        if (huv === null || huv === false) {
          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.setValidators([]);
          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.markAsPristine();
          this.providerForm.get('providerVisitFormValues')?.get('nextProviderVisit')?.markAsUntouched();

          this.providerForm.patchValue({ providerVisitFormValues: { nextProviderVisit: null } });
        } else {
          this.providerForm
            .get('providerVisitFormValues')
            ?.get('nextProviderVisit')
            ?.setValidators([Validators.required, minDateValidator(this.today)]);
        }
      });
  }

  changeSelectedProvider(pId: number) {
    this.providerForm.patchValue({ selectedProviderId: pId });
  }

  onSubmit(): boolean {
    const selectedProviderForm = this.providerForm.get('selectedProviderId')!;
    const providerVisitsForm = this.providerForm.get('providerVisitFormValues')!;

    if (!selectedProviderForm.valid || !providerVisitsForm.valid) {
      validateAllFormFields(selectedProviderForm);
      validateAllFormFields(providerVisitsForm);
      return false;
    }

    return true;
  }

  showModal() {
    this.displayModal = true;
  }

  hideModal() {
    this.displayModal = false;
  }

  onCancelNewProviderForm() {
    if (this.newProviderFormInitialValues) {
      this.newProviderForm.setValue(this.newProviderFormInitialValues);
      this.newProviderForm.markAsPristine();
      this.newProviderForm.markAsUntouched();
    } else {
      this.newProviderForm.reset();
    }
  }

  onSubmitNewProviderForm() {
    if (!this.newProviderForm.valid) {
      validateAllFormFields(this.newProviderForm);
      return;
    }

    this.store.dispatch(new SetNewProviderUserData(this.newProviderForm.value)).subscribe(() => {
      this.changeSelectedProvider(-1);
    });

    this.newProviderFormInitialValues = this.newProviderForm.value;

    this.hideModal();
  }
}
